import {
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query"

import {
  handleMutationError,
  handleQueryError,
} from "@/lib/auth/session-expiry"

/**
 * The app's ``QueryClient``.
 *
 * Every query and mutation funnels through ``apiFetch`` (which throws a
 * typed ``ApiError`` on any non-2xx) and is consumed via a React Query
 * hook, so the ``QueryCache``/``MutationCache`` ``onError`` callbacks are
 * the single place that observes *every* failed call. We attach the
 * logout-on-401 handlers there (design §1.2): a query 401 hard-redirects
 * to ``/signin``; a mutation 401 raises the soft re-auth prompt. Any new
 * feature inherits this for free — no per-hook wiring.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: handleQueryError }),
    mutationCache: new MutationCache({
      // Thread the failing mutation's ``meta`` so the handler can honour an
      // opt-out (e.g. the deliberate Sign out mutation).
      onError: (error, _variables, _context, mutation) =>
        handleMutationError(error, mutation.meta),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  })
}
